import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Card } from 'reactstrap';
import Loading from 'react-loading-animation';
import AlertNotFound from '../../components/Card/AlertNotFound';
import privateView from '../../components/hocs/privateView';
import Analytics from '../../services/Analytics';
import { normalizeError } from '../../utils';
import {
  getCustomerReviewDetail,
  moderateCustomerReview,
} from '../../actions/customerReviewAction';
import { getResponseStatus } from './CustomerReviewCreate.helpers';
import {
  AWAITING_REVIEW_STATUS,
  DECISION_CONFIRMATIONS,
  DECISION_OUTCOME_COLORS,
  DECISION_SUCCESS_MESSAGES,
  DEFAULT_ERROR_404_MSG,
  DEFAULT_ERROR_500_MSG,
  MODERATION_FALLBACK_MSG,
} from './CustomerReviewDetail.helpers';
import ModerationCard from './ModerationCard';
import ModerationConfirmModal from './ModerationConfirmModal';
import ReviewCard from './ReviewCard';
import ReviewOrderCard from './ReviewOrderCard';
import styles from './CustomerReviewDetail.module.css';

const CardLoader = () => (
  <Card body>
    <Loading />
    <p className="text-center">Please wait, fetching your data...</p>
  </Card>
);

const CardError = ({ message }) => (
  <Card body>
    <AlertNotFound message={message} />
    <Button onClick={() => window.location.reload()}>Try to Refresh</Button>
  </Card>
);

CardError.propTypes = {
  message: PropTypes.string,
};

const CustomerReviewDetail = props => {
  const { dataReview, getDetail, moderate, injectedProps, location, match } = props;
  const reviewId = match.params.id;

  const [pendingDecision, setPendingDecision] = useState(null);
  const [submittingDecision, setSubmittingDecision] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [reviewGone, setReviewGone] = useState(false);
  const pageViewRecordedRef = useRef(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    setPendingDecision(null);
    setSubmittingDecision(null);
    setOutcome(null);
    setReviewGone(false);
    getDetail(reviewId);
  }, [getDetail, reviewId]);

  useEffect(() => {
    if (pageViewRecordedRef.current) return;

    pageViewRecordedRef.current = true;
    Analytics.recordPageView('customer_review_detail', injectedProps.email, {
      url: window.location.href,
      location,
      match,
    });
  }, [injectedProps.email, location, match]);

  const review = dataReview.data;
  const isLoaded = Boolean(review) && String(review.id) === String(reviewId);
  const fetchError = (
    dataReview.error && String(dataReview.error.id) === String(reviewId)
      ? dataReview.error
      : null
  );

  const dismissOutcome = () => setOutcome(null);

  const handleConfirm = async () => {
    const decision = pendingDecision;
    // A ref, not the state above: two clicks in one tick would both read the
    // pre-update state and fire the request twice.
    if (!decision || submittingRef.current) return;
    submittingRef.current = true;

    // The design shows progress on the moderation card itself, so the
    // confirmation steps aside and the two buttons carry the in-flight state.
    setPendingDecision(null);
    setSubmittingDecision(decision);
    setOutcome(null);

    try {
      const res = await moderate(reviewId, decision);
      // A null response means a 401 already signed the admin out.
      if (res) {
        setOutcome({
          color: DECISION_OUTCOME_COLORS[decision],
          message: DECISION_SUCCESS_MESSAGES[decision],
        });
      }
    } catch (error) {
      const status = getResponseStatus(error);
      const normalized = normalizeError(error);
      const apiMessage = normalized.error && normalized.error.message;
      setOutcome({
        color: 'danger',
        message: (status === 409 || status === 404) && apiMessage
          ? apiMessage
          : MODERATION_FALLBACK_MSG,
        onDismiss: dismissOutcome,
      });
      if (status === 409) getDetail(reviewId); // reload, alert stays put
      if (status === 404) setReviewGone(true);
    } finally {
      submittingRef.current = false;
      setSubmittingDecision(null);
    }
  };

  return (
    <div>
      {fetchError ? (
        <CardError
          message={fetchError.status_code === 404 ? DEFAULT_ERROR_404_MSG : DEFAULT_ERROR_500_MSG}
        />
      ) : !isLoaded ? (
        <CardLoader />
      ) : (
        <div className={styles.detailRow}>
          <div className={styles.mainCol}>
            <ReviewCard outcome={outcome} review={review} reviewId={reviewId} />
          </div>
          <div className={styles.sideCol}>
            <ReviewOrderCard review={review} />
            <ModerationCard
              onDecide={setPendingDecision}
              review={review}
              showActions={review.status === AWAITING_REVIEW_STATUS && !reviewGone}
              submittingDecision={submittingDecision}
            />
          </div>
        </div>
      )}
      <ModerationConfirmModal
        body={pendingDecision ? DECISION_CONFIRMATIONS[pendingDecision] : ''}
        isLoading={Boolean(submittingDecision)}
        isOpen={Boolean(pendingDecision)}
        onCancel={() => setPendingDecision(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

CustomerReviewDetail.propTypes = {
  dataReview: PropTypes.shape({
    data: PropTypes.object,
    isFetch: PropTypes.bool,
    isSubmit: PropTypes.bool,
    error: PropTypes.object,
  }).isRequired,
  getDetail: PropTypes.func.isRequired,
  moderate: PropTypes.func.isRequired,
  injectedProps: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

const mapStateToProps = ({
  customerReview: { detail },
  auth: { isFetch, roles, isLogged, id, email },
}) => ({
  dataReview: detail,
  guardData: { isFetch, roles, isLogged, id, email },
});

const mapDispatchToProps = {
  getDetail: getCustomerReviewDetail,
  moderate: moderateCustomerReview,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(privateView(CustomerReviewDetail, ['supervisor']));
